import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Quote, BadgeCheck, Send, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const Stars = ({ value = 0, size = 16, onSelect }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        onClick={onSelect ? () => onSelect(n) : undefined}
        className={`${n <= value ? "fill-gold text-gold" : "text-slate-300"} ${onSelect ? "cursor-pointer transition-transform hover:scale-110" : ""}`}
        data-testid={onSelect ? `review-star-${n}` : undefined}
      />
    ))}
  </span>
);

export const ReviewsSection = ({ dark = false }) => {
  const [data, setData] = useState({ average: 0, count: 0, reviews: [] });
  const [form, setForm] = useState({ name: "", rating: 0, text: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) { toast.error("Veuillez renseigner votre nom et votre avis."); return; }
    if (!form.rating) { toast.error("Veuillez attribuer une note."); return; }
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/reviews", form);
      toast.success(res.message || "Merci pour votre avis !");
      setForm({ name: "", rating: 0, text: "", email: "" });
      load();
    } catch (err) { toast.error(apiErrorMessage(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const base = dark ? "bg-navy" : "bg-[#F8F9FA]";
  const heading = dark ? "text-white" : "text-navy";
  const sub = dark ? "text-white/70" : "text-muted-foreground";
  const card = dark ? "bg-white/[0.05] border-white/10" : "bg-white border-border";
  const cardText = dark ? "text-white/85" : "text-slate-700";
  const cardName = dark ? "text-white" : "text-navy";

  return (
    <section data-testid="reviews-section" className={`relative overflow-hidden ${base}`}>
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="text-center">
          <span className="inline-block text-xs sm:text-sm uppercase tracking-[0.25em] text-gold font-semibold">Ils nous font confiance</span>
          <h2 className={`mt-4 font-heading font-medium text-3xl sm:text-4xl lg:text-5xl ${heading}`}>Avis clients</h2>
          <div className="gold-line mx-auto mt-6" />
          {data.count > 0 && (
            <div className="mt-6 flex items-center justify-center gap-3" data-testid="reviews-average">
              <span className={`font-heading text-3xl ${heading}`}>{data.average.toFixed(1)}</span>
              <Stars value={Math.round(data.average)} size={22} />
              <span className={`text-sm ${sub}`}>· {data.count} avis</span>
            </div>
          )}
        </div>

        {data.reviews.length > 0 && (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-list">
            {data.reviews.map((r, i) => (
              <motion.div
                key={r.id}
                data-testid={`review-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className={`relative rounded-2xl border p-6 ${card}`}
              >
                <Quote size={30} className="text-gold/30 absolute right-5 top-5" />
                <Stars value={r.rating} size={16} />
                <p className={`mt-4 text-sm sm:text-base leading-relaxed ${cardText}`}>“{r.text}”</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className={`font-heading text-lg ${cardName}`}>{r.name}</span>
                  {r.source === "google" && (
                    <span className="inline-flex items-center gap-1 text-xs text-gold font-semibold" title="Avis Google">
                      <BadgeCheck size={14} /> Avis Google
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Laisser un avis */}
        <div className="mt-12 max-w-2xl mx-auto">
          <div className={`rounded-2xl border p-6 sm:p-8 ${card}`}>
            <h3 className={`font-heading text-xl ${cardName}`}>Laissez votre avis</h3>
            <p className={`mt-1 text-sm ${sub}`}>Votre retour aide les futurs candidats à nous faire confiance.</p>
            <form onSubmit={submit} data-testid="review-form" className="mt-5 space-y-4">
              <div className="flex items-center gap-3">
                <span className={`text-sm ${cardText}`}>Votre note :</span>
                <Stars value={form.rating} size={26} onSelect={(n) => setForm({ ...form, rating: n })} />
              </div>
              <input
                data-testid="review-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Votre nom"
                className={`w-full rounded-lg border px-4 py-3 text-sm outline-none focus:border-gold ${dark ? "bg-white/5 border-white/15 text-white placeholder:text-white/40" : "border-border text-navy"}`}
              />
              <input
                data-testid="review-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email (facultatif)"
                className={`w-full rounded-lg border px-4 py-3 text-sm outline-none focus:border-gold ${dark ? "bg-white/5 border-white/15 text-white placeholder:text-white/40" : "border-border text-navy"}`}
              />
              <textarea
                data-testid="review-text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
                placeholder="Partagez votre expérience…" rows={4}
                className={`w-full rounded-lg border px-4 py-3 text-sm outline-none focus:border-gold ${dark ? "bg-white/5 border-white/15 text-white placeholder:text-white/40" : "border-border text-navy"}`}
              />
              <button
                type="submit" data-testid="review-submit" disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold uppercase tracking-wide py-3.5 rounded-full transition-all disabled:opacity-60"
              >
                {submitting ? <><Loader2 size={18} className="animate-spin" /> Envoi…</> : <><Send size={16} /> Publier mon avis</>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
